import axios, { AxiosHeaders, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import * as decode from 'jwt-decode';
import { toast } from 'react-hot-toast';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { FeatureStorageContext } from '@src/core/feature-toggle/feature-storage';
import { LocalStorageKey } from '@src/core/storage/local';
import { RoutePathsEnum, RoutePathsEnumType } from '@src/core/routing/route-paths.enum';
import { LocaleTranslationKeysEnum } from '@src/core/i18n/locale-translation-keys.enum';
import { createClickableSupportTeamErrorMessage } from '@src/server/response-messages';
import { isErrorToastSuppressed } from '@src/server/suppress-error-toast';
import useAuth from '@src/core/auth/components/auth0-wrapper';
import type { DecodedToken } from '@src/core/auth/components/types/accessToken';

const errorToasterMap: Record<number, LocaleTranslationKeysEnum> = {
  504: LocaleTranslationKeysEnum.ToasterMessageTimeoutError,
  400: LocaleTranslationKeysEnum.ToasterMessageBadRequestError,
};

const errorNavigateMap: Record<number, RoutePathsEnumType> = {
  401: RoutePathsEnum.Error401,
  403: RoutePathsEnum.Error403,
  404: RoutePathsEnum.Error404,
  500: RoutePathsEnum.Error500,
};

// This client is created above the TokenProvider, so emulation is read from the request's token.
const isEmulatedWrite = (config?: RawAxiosRequestConfig): boolean => {
  const authorization = String(AxiosHeaders.from(config?.headers).get('Authorization') ?? '');
  const token = authorization.replace(/^Bearer /, '');

  if (!token || (config?.method ?? 'get').toLowerCase() === 'get') {
    return false;
  }

  return Boolean((decode.default(token) as DecodedToken).ciam_emulated_user?.assumedEmail);
};

export const useHttpClient = (baseUrl: string, defaultConfig: RawAxiosRequestConfig) => {
  const { LocalStorageService } = useContext(FeatureStorageContext);
  const navigate = useNavigate();
  const { t: translateText } = useTranslation();

  const { logout } = useAuth();

  const [clientConfig, setClientConfig] = useState<RawAxiosRequestConfig>(defaultConfig);

  const cmpHttpClient: AxiosInstance = useMemo(() => {
    const httpClient = axios.create({
      baseURL: baseUrl,
      ...clientConfig,
    });

    httpClient.interceptors.request.use(
      (config) => {
        // Do something before request is sent
        if (LocalStorageService.getItem(LocalStorageKey.IsErrorNotifierEnabled)) {
          // eslint-disable-next-line no-console
          toast.loading(`Loading: ${config.url}`);
        }

        return config;
      },
      (error) =>
        // Do something with request error
        Promise.reject(error)
    );

    // React immediately on server response, but before directing the response to client logic
    httpClient.interceptors.response.use(
      (response) => {
        if (LocalStorageService.getItem(LocalStorageKey.IsErrorNotifierEnabled)) {
          // eslint-disable-next-line no-console
          toast.success(`Success: ${response.config.url}`);
        }
        return response;
      },
      async (error) => {
        // Callers can opt out of the interceptor's error toasts (via the suppress-error-toast
        // request header) — e.g. a react-query call that retries on its own and surfaces the
        // final error itself. Navigation and logout still run — those are behavioural, not
        // just cosmetic notifications.
        const suppressErrorToast = isErrorToastSuppressed(error?.config);

        if (error.code === 'ECONNABORTED') {
          if (!suppressErrorToast) {
            toast.error(translateText(LocaleTranslationKeysEnum.ToasterMessageTimeoutError), {
              id: error.message,
            });
          }
          return Promise.reject(error);
        }

        if (!error || !error.response) {
          if (!suppressErrorToast) {
            createClickableSupportTeamErrorMessage(
              translateText(LocaleTranslationKeysEnum.ToasterMessageUnknownError),
              translateText(LocaleTranslationKeysEnum.SwoErrorWithUserAccessPermissionsSupport)
            );
          }
          return Promise.reject(error);
        }

        if (error.response.status === 401) {
          await logout();
          return Promise.reject(error);
        }

        if (error.response.status === 403 && isEmulatedWrite(error.config)) {
          if (!suppressErrorToast) {
            toast.error(translateText(LocaleTranslationKeysEnum.EmulationReadOnly), {
              id: error.message,
            });
          }
          return Promise.reject(error);
        }

        const errorMessage: string = errorToasterMap[error.response.status];
        const errorRoute: string = errorNavigateMap[error.response.status];

        if (errorMessage && !suppressErrorToast) {
          toast.error(translateText(errorMessage), {
            id: error.message,
          });
        }

        if (errorRoute) {
          navigate(errorRoute, { replace: true });
        }

        if (!errorMessage && !errorRoute && !suppressErrorToast) {
          createClickableSupportTeamErrorMessage(
            translateText(LocaleTranslationKeysEnum.ToasterMessageUnknownError),
            translateText(LocaleTranslationKeysEnum.SwoErrorWithUserAccessPermissionsSupport)
          );
        }

        return Promise.reject(error);
      }
    );
    return httpClient;
  }, [baseUrl, clientConfig, LocalStorageService, navigate, translateText, logout]);

  const updateClientConfiguration = useCallback((updates: Partial<RawAxiosRequestConfig>) => {
    setClientConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  return { updateClientConfiguration, cmpHttpClient };
};
